import * as signalR from '@microsoft/signalr';

class SignalRService {
  constructor() {
    this.connection = null;
    this.listeners = {};
    this.isConnected = false;
  }

  // Suscribirse a un evento
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  // Desuscribirse de un evento
  off(event, callback) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  // Emitir un evento localmente a todos los escuchas registrados
  emit(event, data) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (err) {
        //console.error(`[SignalRService] Error en callback para el evento ${event}:`, err);
      }
    });
  }

  // Iniciar la conexión persistente con SignalR
  async connect(params = {}) {
    if (this.connection) {
      //console.log("[SignalRService] La conexión ya existe. Desconectando para aplicar parámetros nuevos...");
      await this.disconnect();
    }

    // Generar la URL del Hub a partir de REACT_APP_API_URL_WSS o fallback a puerto 5000
    let baseWss = process.env.REACT_APP_API_URL_WSS || 'ws://localhost:5000/ws';
    let baseHttp = baseWss.replace(/^ws(s)?:\/\//, 'http$1://');
    let hubUrl = baseHttp.endsWith('/ws') ? baseHttp.replace(/\/ws$/, '/hub/entertainment') : baseHttp;

    // Agregar parámetros de query string (tipo, uid, zona, roomId)
    const queryParts = [];
    if (params.tipo) queryParts.push(`tipo=${encodeURIComponent(params.tipo)}`);
    if (params.uid) queryParts.push(`uid=${encodeURIComponent(params.uid)}`);
    const zonaParam = params.zona || params.userZone;
    if (zonaParam) queryParts.push(`zona=${encodeURIComponent(zonaParam)}`);
    if (params.roomId) queryParts.push(`roomId=${encodeURIComponent(params.roomId)}`);

    if (queryParts.length > 0) {
      hubUrl += `?${queryParts.join('&')}`;
    }

    //console.log(`[SignalRService] Conectando a: ${hubUrl} | roomId recibido: ${params.roomId || 'ninguno'}`);

    const customRetryPolicy = {
      nextRetryDelayInMilliseconds(retryContext) {
        // Reintentos infinitos (nunca devuelve null)
        const baseDelay = 1000; // Retraso inicial de 1000ms
        const maxDelay = 15000; // Máximo de 15000ms
        const exponentialDelay = baseDelay * Math.pow(2, Math.min(retryContext.previousRetryCount, 4));
        const jitter = (Math.random() - 0.5) * 2000; // Jitter aleatorio de ±1000ms
        const nextDelay = Math.min(maxDelay, exponentialDelay) + jitter;
        return Math.max(500, nextDelay); // Evitar valores negativos o excesivamente bajos
      }
    };

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect(customRetryPolicy)
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Mapear el recibimiento de mensajes genéricos del Hub al Event Emitter local
    this.connection.on("ReceiveMessage", (message) => {
      try {
        let envelope = null;
        
        // Extraer si viene en arguments en formato nativo de SignalR
        if (message && message.arguments && Array.isArray(message.arguments) && message.arguments.length > 0) {
          const rawArg = message.arguments[0];
          envelope = typeof rawArg === 'string' ? JSON.parse(rawArg) : rawArg;
        } else {
          envelope = typeof message === 'string' ? JSON.parse(message) : message;
        }

        //console.log("[SignalRService] Mensaje recibido (limpio):", envelope);

        if (envelope && envelope.type) {
          // Despachar el evento basándose en su campo 'type' (pasando payload o data)
          const dataPayload = envelope.payload !== undefined ? envelope.payload : envelope.data;
          this.emit(envelope.type, dataPayload);
          // Emitir el mensaje completo para componentes que escuchen todo de forma genérica
          this.emit("message", envelope);
        }
      } catch (e) {
        //console.error("[SignalRService] Error al parsear mensaje entrante:", e);
      }
    });

    this.connection.onreconnecting((error) => {
      this.isConnected = false;
      //console.warn("[SignalRService] Conexión perdida. Intentando reconexión automática...", error);
      this.emit('reconnecting', { error });
    });

    this.connection.onreconnected((connectionId) => {
      this.isConnected = true;
      //console.log("[SignalRService] Conexión restablecida con éxito. ConnectionId:", connectionId);
      this.emit('reconnected', { connectionId });
    });

    this.connection.onclose((error) => {
      this.isConnected = false;
      //console.warn("[SignalRService] Conexión cerrada de forma definitiva.", error);
      this.emit('close', { error });
    });

    try {
      await this.connection.start();
      this.isConnected = true;
      //console.log("[SignalRService] Conexión establecida correctamente.");
    } catch (err) {
      this.isConnected = false;
      //console.error("[SignalRService] Error al iniciar la conexión del Hub:", err);
      throw err;
    }
  }

  // Detener la conexión
  async disconnect() {
    if (!this.connection) return;

    try {
      await this.connection.stop();
      //console.log("[SignalRService] Desconectado limpiamente.");
    } catch (err) {
      //console.error("[SignalRService] Error al detener la conexión del Hub:", err);
    } finally {
      this.connection = null;
      this.isConnected = false;
    }
  }

  // Invocar un método en el Hub
  async invoke(methodName, ...args) {
    if (!this.connection || !this.isConnected) {
      //console.warn(`[SignalRService] No se puede invocar ${methodName} porque no está conectado.`);
      return;
    }
    return this.connection.invoke(methodName, ...args);
  }
}

const signalRServiceInstance = new SignalRService();
export default signalRServiceInstance;
